import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Button, Typography, DialogActions, TextField, Divider, Checkbox, Box } from '@mui/material';
import { ContestPrize } from '@beable/roster-backend-client-v1';
import BaseDialog from '../../Common/BaseDialog';
import InputAdornment from '@mui/material/InputAdornment';

const CourseSetLabel = styled(Typography)(({ theme }) => ({
  paddingRight: theme.spacing(1),
  fontSize: '18px',
}));

const GridContainer = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

interface EditContestPrizeDialogProps {
  contestPrize?: Partial<ContestPrize>;
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (contestPrize: ContestPrize) => void;
}

export default function EditContestPrizeDialog({ contestPrize = {}, title: dialogTitle, isOpen, onClose, onSave }: EditContestPrizeDialogProps) {
  
  const { id = '', title = '', monetaryValue = 0, points = 0, imageUrl = '', displayNameEn = '', displayNameEs = '', isCustom = false } = contestPrize;

  const [contestPrizeValues, setContestPrizeValues] = useState<ContestPrize>({
    id,
    title,
    monetaryValue,
    points,
    imageUrl,
    displayNameEn,
    displayNameEs,
    isCustom,
  });

  function getSampleText(prize: Partial<ContestPrize>) {
    if (prize.monetaryValue && prize.points && prize.isCustom) {
      return `You've earned ${prize.points} points, ${prize.points} Beable Bucks, a $${prize.monetaryValue} gift card${prize.displayNameEn}`; 
    }

    if (prize.monetaryValue && prize.points) {
      return `You've earned ${prize.points} points, ${prize.points} Beable Bucks, and a $${prize.monetaryValue} gift card.`;
    }

    if (prize.monetaryValue) {
      return `You've earned a $${prize.monetaryValue} gift card.`;
    }

    if (prize.isCustom) {
      return `You've earned ${prize.points} points, ${prize.points} Beable Bucks${prize.displayNameEn}`;
    }
    
    return `You've earned ${prize.points} points and ${prize.points} Beable Bucks.`;
  }

  function getValue(event: React.ChangeEvent<HTMLInputElement>, isNumber = false, isCheckBox = false) {
    if (isNumber) {
      return Number(event.target.value);
    }
    if (isCheckBox) {
      return event.target.checked;
    }
    return event.target.value;
  }

  const handleChange = (prop: keyof ContestPrize, isNumber = false, isCheckBox = false) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = getValue(event, isNumber, isCheckBox);
    setContestPrizeValues({ ...contestPrizeValues, [prop]: value });
  };

  const saveOnClick = () => {
    onSave(contestPrizeValues);
  };

  const DialogContent = () => {
    return (
      <>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={4}>
            <CourseSetLabel>Title</CourseSetLabel>
          </Grid>
          <Grid size={5}>
            <TextField
              variant="outlined"
              onChange={handleChange('title')}
              defaultValue={contestPrizeValues.title}
              fullWidth={true}
              size='small'
            />
          </Grid>
        </GridContainer>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={4}>
            <CourseSetLabel>Gift Card Monetary Value</CourseSetLabel>
          </Grid>
          <Grid size={5}>
            <TextField
              onChange={handleChange('monetaryValue', true)}
              defaultValue={contestPrizeValues.monetaryValue}
              fullWidth={true}
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }
              }}
            />
          </Grid>
        </GridContainer>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={4}>
            <CourseSetLabel>Points and Beable Bucks</CourseSetLabel>
          </Grid>
          <Grid size={5}>
            <TextField
              variant="outlined"
              onChange={handleChange('points', true)}
              defaultValue={contestPrizeValues.points}
              fullWidth={true}
              size='small'
            />
          </Grid>
        </GridContainer>
        <Divider/>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={1}>
            <Checkbox
              checked={!!contestPrizeValues.isCustom}
              onChange={handleChange('isCustom', false, true)}
              name='isCustom'
              color='primary'
            />
          </Grid>
          <Grid size={9}>
            <CourseSetLabel>Custom Prize</CourseSetLabel>
          </Grid>
        </GridContainer>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={5}>
            <CourseSetLabel>Display Name - English</CourseSetLabel>
          </Grid>
          <Grid size={5}>
            <TextField
              variant="outlined"
              onChange={handleChange('displayNameEn')}
              defaultValue={contestPrizeValues.displayNameEn}
              fullWidth={true}
              size='small'
            />
          </Grid>
        </GridContainer>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Grid size={5}>
            <CourseSetLabel>Display Name - Spanish</CourseSetLabel>
          </Grid>
          <Grid size={5}>
            <TextField
              variant="outlined"
              onChange={handleChange('displayNameEs')}
              defaultValue={contestPrizeValues.displayNameEs}
              fullWidth={true}
              size='small'
            />
          </Grid>
        </GridContainer>
        <GridContainer container sx={{ alignItems: 'center' }}>
          <Box>{getSampleText(contestPrizeValues)}</Box>
        </GridContainer>
        <Divider/>
        <DialogActions>
          <Button
            variant='contained'
            color='primary'
            aria-label='save'
            data-testid="save-btn"
            onClick={saveOnClick}
          >
            Save
          </Button>
          <Button
            variant='contained'
            color='secondary'
            data-testid="cancel-btn"
            aria-label='cancel'
            onClick={onClose}
          >
            Cancel
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <BaseDialog
      open={isOpen}
      header={dialogTitle}
      showCloseButton={false}
      showCloseIcon={true}
      onClose={onClose}
    >{
      DialogContent()
    }</BaseDialog>
  );
}
